import {
  createElement,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Stagger index for sibling reveals. */
  delay?: number;
  /** Travel distance in px (default 22). */
  y?: number;
  /** Optional horizontal travel for opposing column entrances. */
  x?: number;
  as?: ElementType;
};

function useInViewOnce<T extends Element>(enabled: boolean) {
  const ref = useRef<T | null>(null);
  // Start revealed so SSR/no-JS content stays visible; hide only after client mount.
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setReady(true);
    if (!enabled) {
      setVisible(true);
      return;
    }

    setVisible(false);
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: "-12% 0px -8% 0px", threshold: 0.01 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled]);

  return { ref, revealed: !ready || !enabled || visible };
}

/**
 * Fades content upward when it scrolls into view.
 * Uses CSS + IntersectionObserver instead of motion/react (~200 KiB).
 */
export function Reveal({ children, className, delay = 0, y = 22, x = 0, as = "div" }: RevealProps) {
  const reduced = usePrefersReducedMotion();
  const { ref, revealed } = useInViewOnce<HTMLElement>(!reduced);

  const style: CSSProperties | undefined = reduced
    ? undefined
    : ({
        "--reveal-x": `${x}px`,
        "--reveal-y": `${y}px`,
        transitionDelay: delay ? `${delay}s` : undefined,
      } as CSSProperties);

  return createElement(
    as,
    {
      ref,
      className: [className, reduced ? undefined : "reveal-motion", revealed ? "is-revealed" : null]
        .filter(Boolean)
        .join(" "),
      style,
    },
    children,
  );
}

/** Image reveal with a clip-path mask wipe. */
export function MaskReveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, revealed } = useInViewOnce<HTMLDivElement>(!reduced);

  if (reduced) return <div className={className}>{children}</div>;

  return (
    <div
      ref={ref}
      className={["reveal-mask", revealed ? "is-revealed" : null, className]
        .filter(Boolean)
        .join(" ")}
      style={{ transitionDelay: delay ? `${delay}s` : undefined }}
    >
      {children}
    </div>
  );
}
